import { INPUT_TYPE, THEME_COLOR } from "./indicatorTypes";

// Line width in the Styles tab of PO (po-chart-visual-spec.md §5.4): 1px…5px
const MIN_LINE_WIDTH_PX = 1;
const MAX_LINE_WIDTH_PX = 5;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const DECIMAL_BASE = 10;

// `value` with at most `precision` decimals
const roundTo = (value, precision) => {
  const factor = DECIMAL_BASE ** precision;

  return Math.round(value * factor) / factor;
};

// A level value within `rules` ({ min, max, precision }), as it is when there are none
const normalizeLevelValue = (value, rules) =>
  rules ? roundTo(clamp(value, rules.min, rules.max), rules.precision) : value;

const normalizeInput = (input, value) => {
  if (input.type === INPUT_TYPE.SELECT) {
    return input.options.includes(value) ? value : input.defaultValue;
  }

  const number = Number(value);

  if (!Number.isFinite(number)) return input.defaultValue;

  return roundTo(clamp(number, input.min, input.max), input.precision);
};

/**
 * Inputs of an indicator: the PO defaults with the given values over them;
 * unknown keys are dropped, numbers kept within their limits.
 *
 * @param {object} definition - from the registry
 * @param {object} [values] - { [input key]: value }
 * @returns {object} { [input key]: value } for every input
 */
export const normalizeInputs = (definition, values = {}) =>
  Object.fromEntries(
    definition.inputs.map((input) => [
      input.key,
      input.key in values ? normalizeInput(input, values[input.key]) : input.defaultValue,
    ]),
  );

// A PALETTE name or a THEME_COLOR value → CSS colour
const themeCss = (name, theme) => (theme.palette[name] || theme.colors[name])?.css || name;

const normalizeWidth = (width, fallback) => {
  const number = Number(width);

  return Number.isFinite(number) ? clamp(Math.round(number), MIN_LINE_WIDTH_PX, MAX_LINE_WIDTH_PX) : fallback;
};

// Levels typed in the Styles tab: numbers only (within the levelRules of the definition), colour and width of the level in their place by default
const normalizeLevels = (levels, baseLevels, theme, rules) => {
  const validLevels = levels.filter((item) => Number.isFinite(Number(item?.value)));

  return validLevels.map((item, index) => ({
    value: normalizeLevelValue(Number(item.value), rules),
    color: item.color || baseLevels[index]?.color || themeCss(THEME_COLOR.LEVEL, theme),
    width: normalizeWidth(item.width, baseLevels[index]?.width ?? MIN_LINE_WIDTH_PX),
    isVisible: item.isVisible ?? true,
  }));
};

/**
 * Styles of an indicator as the Styles tab shows them: per plot
 * { isVisible, color, downColor, width } with CSS colours, per level
 * { value, color, width, isVisible }. The PO defaults with `changes` over them.
 *
 * @param {object} definition
 * @param {{ palette: object, colors: object }} theme - engine theme (theme.js)
 * @param {object} [current] - styles to change; the defaults when absent
 * @param {{ plots?: object, levels?: object[] }} [changes]
 * @returns {{ plots: object, levels: object[] }}
 */
export const mergeStyles = (definition, theme, current = null, changes = {}) => {
  const plots = {};

  definition.plots.forEach((plot) => {
    const base = current?.plots[plot.key] || {
      isVisible: true,
      color: themeCss(plot.color, theme),
      downColor: plot.downColor ? themeCss(plot.downColor, theme) : null,
      width: plot.width,
    };
    const change = changes.plots?.[plot.key] || {};

    plots[plot.key] = {
      isVisible: change.isVisible ?? base.isVisible,
      color: change.color || base.color,
      downColor: change.downColor || base.downColor,
      width: plot.width ? normalizeWidth(change.width, base.width) : 0,
    };
  });

  const baseLevels =
    current?.levels ||
    definition.levels.map(({ value, color, width }) => ({
      value,
      color: themeCss(color, theme),
      width,
      isVisible: true,
    }));
  const levels = Array.isArray(changes.levels)
    ? normalizeLevels(changes.levels, baseLevels, theme, definition.levelRules)
    : baseLevels;

  return { plots, levels };
};

/**
 * Bars a plot is moved right (left when negative), e.g. the Alligator jaws.
 *
 * @param {{ shift: number | ((inputs: object) => number) }} plot
 * @param {object} inputs
 * @returns {number}
 */
export const plotShift = (plot, inputs) =>
  Math.round((typeof plot.shift === "function" ? plot.shift(inputs) : plot.shift) || 0);
